import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { apiRequest, setAuthToken } from "@/lib/api";

export type UserRole = "admin" | "manager" | "salesperson" | "sysadmin";

interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  storeLocation: string;
  pin?: string;
  storeId?: string;
  isSysAdmin?: boolean;
}

export interface RegisteredStore {
  id: string;
  storeName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  storeAddress: string;
  city: string;
  state: string;
  zip: string;
  username: string;
  trialStartDate: string;
  trialDays: number;
  plan: "trial" | "active" | "expired";
  disabled?: boolean;
}

interface TrialStatus {
  isOnTrial: boolean;
  isExpired: boolean;
  daysLeft: number;
  trialEndDate: string | null;
  storeName: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  trialStatus: TrialStatus;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithPin: (pin: string, storeUsername: string) => Promise<boolean>;
  logout: () => void;
  registerStore: (store: Omit<RegisteredStore, "id" | "trialStartDate" | "trialDays" | "plan"> & { password: string }) => Promise<{ success: boolean; error?: string }>;
  activateSubscription: () => void;
  getRegisteredStores: () => RegisteredStore[];
  fetchStores: () => Promise<void>;
  updateStoreStatus: (storeId: string, updates: { plan?: RegisteredStore["plan"]; disabled?: boolean }) => Promise<void>;
  extendTrial: (storeId: string, extraDays: number) => Promise<void>;
  deleteStore: (storeId: string) => Promise<void>;
  updateStoreProfile: (storeId: string, updates: Record<string, string>) => Promise<{ success: boolean; error?: string }>;
  changePassword: (storeId: string, currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

type Permission =
  | "team:manage"
  | "products:import"
  | "products:clear"
  | "customers:delete"
  | "customers:reassign"
  | "settings:store"
  | "settings:ai"
  | "dashboard:team_stats"
  | "followups:audit_log";

const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: [
    "team:manage",
    "products:import",
    "products:clear",
    "customers:delete",
    "customers:reassign",
    "settings:store",
    "settings:ai",
    "dashboard:team_stats",
    "followups:audit_log",
  ],
  manager: [
    "products:import",
    "products:clear",
    "customers:delete",
    "customers:reassign",
    "settings:store",
    "settings:ai",
    "dashboard:team_stats",
    "followups:audit_log",
  ],
  salesperson: [],
  sysadmin: [],
};

export function hasPermission(role: UserRole | undefined, permission: Permission): boolean {
  if (!role) return false;
  return (ROLE_PERMISSIONS[role] || []).includes(permission);
}

export function getRoleLabel(role: UserRole): string {
  switch (role) {
    case "admin": return "Admin";
    case "manager": return "Manager";
    case "salesperson": return "Sales";
    case "sysadmin": return "System Admin";
  }
}

export function getRoleColor(role: UserRole): string {
  switch (role) {
    case "admin": return "#ef4444";
    case "manager": return "#8b5cf6";
    case "salesperson": return "#1b6ef3";
    case "sysadmin": return "#f59e0b";
  }
}

function computeTrialStatus(store: RegisteredStore | null): TrialStatus {
  if (!store) {
    return { isOnTrial: false, isExpired: false, daysLeft: 0, trialEndDate: null, storeName: null };
  }

  if (store.plan === "active") {
    return { isOnTrial: false, isExpired: false, daysLeft: 0, trialEndDate: null, storeName: store.storeName };
  }

  const start = new Date(store.trialStartDate);
  const end = new Date(start.getTime() + store.trialDays * 24 * 60 * 60 * 1000);
  const now = new Date();
  const msLeft = end.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
  const isExpired = msLeft <= 0;

  return {
    isOnTrial: !isExpired,
    isExpired,
    daysLeft,
    trialEndDate: end.toISOString(),
    storeName: store.storeName,
  };
}

const DEFAULT_TRIAL_STATUS: TrialStatus = {
  isOnTrial: false,
  isExpired: false,
  daysLeft: 0,
  trialEndDate: null,
  storeName: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [trialStatus, setTrialStatus] = useState<TrialStatus>(DEFAULT_TRIAL_STATUS);
  const [storesCache, setStoresCache] = useState<RegisteredStore[]>([]);

  const fetchStores = useCallback(async () => {
    try {
      const stores = await apiRequest<RegisteredStore[]>("/stores");
      setStoresCache(stores);
    } catch {
      setStoresCache([]);
    }
  }, []);

  const refreshTrialFromServer = useCallback(async (storeId: string) => {
    try {
      const store = await apiRequest<RegisteredStore>(`/stores/${storeId}`);
      setTrialStatus(computeTrialStatus(store));
    } catch {
      setTrialStatus(DEFAULT_TRIAL_STATUS);
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("auth_user");
    const token = localStorage.getItem("auth_token");
    if (stored && token) {
      try {
        const u = JSON.parse(stored) as User;
        setUser(u);
        if (u.isSysAdmin) {
          fetchStores();
        } else if (u.storeId) {
          refreshTrialFromServer(u.storeId);
        }
      } catch {}
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!user?.storeId) return;
    const interval = setInterval(() => {
      refreshTrialFromServer(user.storeId!);
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.storeId]);

  const persistUser = (u: User, token?: string) => {
    setUser(u);
    localStorage.setItem("auth_user", JSON.stringify(u));
    if (token) {
      setAuthToken(token);
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await apiRequest<{ found: boolean; store?: RegisteredStore; token?: string; isSysAdmin?: boolean; user?: any; error?: string }>("/stores/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      if (result.found && result.isSysAdmin && result.token) {
        const u: User = {
          id: "sysadmin",
          name: result.user?.name || "System Admin",
          email: result.user?.email || email,
          role: "sysadmin",
          storeLocation: "Showroom AiPro",
          isSysAdmin: true,
        };
        persistUser(u, result.token);
        setTrialStatus(DEFAULT_TRIAL_STATUS);
        await fetchStores();
        return { success: true };
      }

      if (result.found && result.store && result.token) {
        const store = result.store;
        const status = computeTrialStatus(store);
        const u: User = {
          id: store.id,
          name: `${store.firstName} ${store.lastName}`,
          email: store.email,
          role: "admin",
          storeLocation: store.storeName,
          storeId: store.id,
        };
        persistUser(u, result.token);
        setTrialStatus(status);
        return { success: true };
      }

      if (result.found && result.error) {
        return { success: false, error: result.error };
      }
    } catch {}

    return { success: false, error: "invalid_credentials" };
  };

  const loginWithPin = async (pin: string, storeUsername: string) => {
    try {
      const result = await apiRequest<{ found: boolean; member?: any; store?: RegisteredStore; token?: string; error?: string }>("/team/pin-login", {
        method: "POST",
        body: JSON.stringify({ pin, username: storeUsername }),
      });

      if (result.found && result.member && result.store && result.token) {
        const member = result.member;
        const store = result.store;
        const u: User = {
          id: member.id,
          name: member.name,
          email: member.email,
          role: member.role,
          storeLocation: store.storeName,
          storeId: store.id,
        };
        persistUser(u, result.token);
        const status = computeTrialStatus(store);
        setTrialStatus(status);
        return true;
      }

      if (result.found && result.error) {
        return false;
      }
    } catch {}

    return false;
  };

  const logout = () => {
    setUser(null);
    setTrialStatus(DEFAULT_TRIAL_STATUS);
    localStorage.removeItem("auth_user");
    setAuthToken(null);
  };

  const registerStore = async (data: Omit<RegisteredStore, "id" | "trialStartDate" | "trialDays" | "plan"> & { password: string }): Promise<{ success: boolean; error?: string }> => {
    try {
      await apiRequest("/stores/register", {
        method: "POST",
        body: JSON.stringify(data),
      });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || "Registration failed" };
    }
  };

  const activateSubscription = async () => {
    if (!user?.storeId) return;
    try {
      await apiRequest(`/stores/${user.storeId}`, {
        method: "PUT",
        body: JSON.stringify({ plan: "active" }),
      });
      setTrialStatus({
        isOnTrial: false,
        isExpired: false,
        daysLeft: 0,
        trialEndDate: null,
        storeName: trialStatus.storeName,
      });
    } catch {}
  };

  const getRegisteredStores = (): RegisteredStore[] => {
    return storesCache;
  };

  const updateStoreStatus = async (storeId: string, updates: { plan?: RegisteredStore["plan"]; disabled?: boolean }) => {
    try {
      await apiRequest(`/stores/${storeId}`, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      await fetchStores();
    } catch {}
  };

  const extendTrial = async (storeId: string, extraDays: number) => {
    try {
      await apiRequest(`/stores/${storeId}/extend-trial`, {
        method: "POST",
        body: JSON.stringify({ days: extraDays }),
      });
      await fetchStores();
    } catch {}
  };

  const deleteStore = async (storeId: string) => {
    try {
      await apiRequest(`/stores/${storeId}`, {
        method: "DELETE",
      });
      await fetchStores();
    } catch {}
  };

  const updateStoreProfile = async (storeId: string, updates: Record<string, string>): Promise<{ success: boolean; error?: string }> => {
    try {
      const updatedStore = await apiRequest<RegisteredStore>(`/stores/${storeId}`, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (user && user.storeId === storeId) {
        const updatedUser = { ...user, storeLocation: updatedStore.storeName, name: `${updatedStore.firstName} ${updatedStore.lastName}` };
        persistUser(updatedUser);
        setTrialStatus(computeTrialStatus(updatedStore));
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || "Failed to update store profile" };
    }
  };

  const changePassword = async (storeId: string, currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await apiRequest(`/stores/${storeId}/password`, {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || "Failed to change password" };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      trialStatus,
      login,
      loginWithPin,
      logout,
      registerStore,
      activateSubscription,
      getRegisteredStores,
      fetchStores,
      updateStoreStatus,
      extendTrial,
      deleteStore,
      updateStoreProfile,
      changePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
